<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from '@heroicons/vue/24/solid';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

interface Testimonial {
    id: number;
    name: Record<string, string>;
    role: Record<string, string>;
    text: Record<string, string>;
    original_language: string;
}

const props = withDefaults(
    defineProps<{
        testimonials: Testimonial[];
        youtubeId?: string;
    }>(),
    {
        testimonials: () => [],
        youtubeId: 'M7lc1UVf-VE',
    },
);

const { locale, t } = useTranslations(); // Récupère la langue courante (ex: 'fr' ou 'en')

const youtubeEmbedUrl = computed(() => `https://www.youtube-nocookie.com/embed/${props.youtubeId}`);

const carousel = ref<HTMLElement | null>(null);
const activeIndex = ref(0);
let scrollTimer: ReturnType<typeof setTimeout> | null = null;

// Helper pour récupérer le texte traduit ou basculer sur la langue par défaut si vide
function getLocalizedText(field: Record<string, string>): string {
    const currentLang = locale.value || 'fr';
    return field[currentLang] || field['fr'] || '';
}

function getCards() {
    const container = carousel.value;
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>('[data-review-card]'));
}

function syncActiveIndex() {
    const container = carousel.value;
    if (!container) return;

    const cards = getCards();
    if (!cards.length) return;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - container.scrollLeft);
        if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
        }
    });

    activeIndex.value = closestIndex;
}

function scrollToIndex(index: number) {
    const container = carousel.value;
    const cards = getCards();
    const target = cards[index];

    if (!container || !target) return;

    container.scrollTo({
        left: target.offsetLeft,
        behavior: 'smooth',
    });
    activeIndex.value = index;
}

function previousReview() {
    const nextIndex = activeIndex.value <= 0 ? props.testimonials.length - 1 : activeIndex.value - 1;
    scrollToIndex(nextIndex);
}

function nextReview() {
    const nextIndex = activeIndex.value >= props.testimonials.length - 1 ? 0 : activeIndex.value + 1;
    scrollToIndex(nextIndex);
}

function onScroll() {
    if (scrollTimer) clearTimeout(scrollTimer);
    scrollTimer = setTimeout(syncActiveIndex, 16);
}

onMounted(() => {
    syncActiveIndex();
});

onBeforeUnmount(() => {
    if (scrollTimer) clearTimeout(scrollTimer);
});
</script>

<template>
    <section id="experiences" class="bg-primary-lighter py-16">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="rounded-3xl bg-primary-light p-6 shadow-lg sm:p-8 lg:p-10">
                <div class="mb-8 max-w-3xl">
                    <h2 class="mt-3 text-3xl font-bold text-dark md:text-4xl">
                        {{ t('home.experiences.title') }}
                    </h2>
                    <p class="mt-4 text-base leading-7 text-gray-600">
                        {{ t('home.experiences.subtitle') }}
                    </p>
                </div>

                <div class="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                    <!-- Section Vidéo -->
                    <div class="space-y-4">
                        <div class="overflow-hidden rounded-3xl bg-black shadow-xl">
                            <div class="relative aspect-video">
                                <iframe
                                    :src="youtubeEmbedUrl"
                                    :title="t('home.experiences.videoTitle')"
                                    class="absolute inset-0 h-full w-full"
                                    loading="lazy"
                                    referrerpolicy="strict-origin-when-cross-origin"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowfullscreen
                                ></iframe>
                            </div>
                        </div>

                        <div class="rounded-2xl bg-white p-5 shadow-sm">
                            <h3 class="text-lg font-semibold text-dark">
                                {{ t('home.experiences.videoTitle') }}
                            </h3>
                            <p class="mt-2 text-sm leading-6 text-gray-600">
                                {{ t('home.experiences.videoCaption') }}
                            </p>
                        </div>
                    </div>

                    <!-- Section Carousel Avis -->
                    <div class="space-y-4">
                        <div class="flex items-center justify-between gap-4">
                            <h3 class="text-lg font-semibold text-dark">
                                {{ t('home.experiences.carouselLabel') }}
                            </h3>

                            <div class="flex items-center gap-2">
                                <button
                                    type="button"
                                    class="rounded-full bg-white p-2 text-dark shadow-sm transition hover:bg-primary hover:text-white"
                                    :aria-label="t('home.experiences.prev')"
                                    @click="previousReview"
                                    v-if="testimonials.length > 0"
                                >
                                    <ChevronLeftIcon class="h-5 w-5" />
                                </button>
                                <button
                                    type="button"
                                    class="rounded-full bg-white p-2 text-dark shadow-sm transition hover:bg-primary hover:text-white"
                                    :aria-label="t('home.experiences.next')"
                                    @click="nextReview"
                                    v-if="testimonials.length > 0"
                                >
                                    <ChevronRightIcon class="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div
                            ref="carousel"
                            class="no-scrollbar snap-x snap-mandatory overflow-x-auto scroll-smooth"
                            :aria-label="t('home.experiences.carouselLabel')"
                            @scroll.passive="onScroll"
                            v-if="testimonials.length > 0"
                        >
                            <div class="flex gap-4 pb-2">
                                <article
                                    v-for="testimonial in testimonials"
                                    :key="testimonial.id"
                                    data-review-card
                                    class="min-w-full flex-none snap-start rounded-3xl bg-white p-6 shadow-sm transition md:min-w-[calc(50%-0.5rem)]"
                                >
                                    <div class="flex items-start justify-between gap-4">
                                        <div class="min-w-0">
                                            <h4 class="truncate text-base font-semibold text-dark">
                                                {{ getLocalizedText(testimonial.name) }}
                                            </h4>
                                            <p class="text-sm text-gray-500">
                                                {{ getLocalizedText(testimonial.role) }}
                                            </p>
                                        </div>

                                        <!-- Badge de la langue d'origine -->
                                        <span
                                            class="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary uppercase"
                                        >
                                            {{ testimonial.original_language }}
                                        </span>
                                    </div>

                                    <div class="mt-4 flex items-center gap-2">
                                        <div class="flex gap-1 text-amber-400" aria-hidden="true">
                                            <StarIcon v-for="star in 5" :key="`${testimonial.id}-${star}`" class="h-5 w-5" />
                                        </div>
                                        <span
                                            class="text-sm font-semibold text-amber-500"
                                            :aria-label="t('home.experiences.ratingLabel', { rating: 5 })"
                                        >
                                            5/5
                                        </span>
                                    </div>

                                    <blockquote class="mt-4 text-sm leading-6 text-gray-600">"{{ getLocalizedText(testimonial.text) }}"</blockquote>
                                </article>
                            </div>
                        </div>
                        <div v-else class="flex items-center justify-center rounded-3xl bg-white p-6 shadow-sm">
                            <p class="text-sm text-gray-500">
                                {{ t('home.experiences.noTestimonials') }}
                            </p>
                        </div>
                        <div class="flex items-center justify-center gap-2">
                            <button
                                v-for="(testimonial, index) in testimonials"
                                :key="`${testimonial.id}-dot`"
                                type="button"
                                class="h-2.5 rounded-full transition-all duration-300"
                                :class="activeIndex === index ? 'w-8 bg-primary' : 'w-2.5 bg-primary/30 hover:bg-primary/60'"
                                :aria-label="t('home.experiences.goToReview', { index: index + 1 })"
                                :aria-pressed="activeIndex === index"
                                @click="scrollToIndex(index)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
